import React, { useMemo, useState } from 'react'

import * as R from 'ramda'

import {
  Flex,
  IconButton,
  InlineButton,
  StatusMessage,
  styled,
  Table,
  Text,
  UserAvatar,
} from '@driftt/dds'
import { faPenToSquare } from '@driftt/dds.icons/light/faPenToSquare'
import { faUpRightFromSquare } from '@driftt/dds.icons/light/faUpRightFromSquare'

import useDebounce from 'hooks/useDebounce'
import { isNilOrEmpty } from 'utils/ramda'
import { trackEvent } from 'utils/tracking'

import CheckboxFilters from '../CheckboxFilters'
import { EDIT_USER_LINK, MANAGE_TEAMMATES_LINK } from '../constants'
import SalesRepModal from '../SalesRepModal'
import { SalesRepRows } from '../types'

import InformationalTooltip from './InformationalTooltip'
import TableCard from './TableCard'

const SalesRepTable = ({ tableData, isLoading, selectedTeamIds }) => {
  const [filteredData, setFilteredData] = useState<SalesRepRows[]>(tableData)
  const [salesRepRow, setSalesRepRow] = useState<SalesRepRows>(
    {} as SalesRepRows
  )
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchText, setSearchText] = useState('')

  const openModal = (data: SalesRepRows) => {
    setSalesRepRow(data)
    setIsModalOpen(true)
  }

  const getStatus = (status: boolean) => (
    <StatusMessage variant={status ? 'success' : 'error'}>{''}</StatusMessage>
  )

  const InlineNameButton = styled(InlineButton, {
    '& > div': {
      maxWidth: 180,
      clampLines: 2,
      textAlign: 'left',
    },
  })

  const Name = ({ userData }) => (
    <InlineNameButton data-private onClick={() => openModal(userData)}>
      {userData.name}
    </InlineNameButton>
  )

  const openEditUserPage = ({ id }) => {
    const editUserUrl = `${EDIT_USER_LINK}/${id}`
    window.open(id ? editUserUrl : MANAGE_TEAMMATES_LINK, '_blank')
  }

  const debouncedSearchText = useDebounce(searchText, 500)

  const filteredRows = useMemo(() => {
    if (isNilOrEmpty(debouncedSearchText)) {
      return filteredData
    }
    const searchText = debouncedSearchText.toLowerCase()
    return filteredData.filter((row) => {
      const nameMatches =
        !!row.name && row.name.toLowerCase().includes(searchText)
      const emailMatches =
        row.email && row.email.toLowerCase().includes(searchText)
      return nameMatches || emailMatches
    })
  }, [filteredData, debouncedSearchText])

  const firstRowId = useMemo(
    () => !R.isEmpty(filteredRows) && R.path(['id'], filteredRows[0]),
    [filteredRows]
  )

  return (
    <TableCard
      data={filteredData}
      searchText={searchText}
      setSearchText={setSearchText}
    >
      <Flex direction="column" gap={2}>
        <CheckboxFilters
          tableData={tableData}
          setFilteredData={setFilteredData}
          isTeamSelected={selectedTeamIds.length}
        />
        <Table<SalesRepRows>
          rows={filteredRows ?? []}
          columns={[
            {
              header: 'Name',
              value: 'name',
              cell: (data) => (
                <Flex alignItems="center">
                  {isNilOrEmpty(data.name) ? (
                    <UserAvatar size="small" />
                  ) : (
                    <>
                      <UserAvatar size="small" name={data.name} />

                      {R.equals(data.id, firstRowId) ? (
                        <InformationalTooltip>
                          <Name userData={data} />
                        </InformationalTooltip>
                      ) : (
                        <Name userData={data} />
                      )}
                    </>
                  )}
                </Flex>
              ),
              size: 180,
              disableSort: true,
            },
            {
              header: 'Email',
              value: 'email',
              cell: ({ email }) => (
                <Flex width={180}>
                  <Text isPrivate truncate={true}>
                    {email}
                  </Text>
                </Flex>
              ),
              size: 180,
              disableSort: true,
            },
            {
              header: 'Team',
              value: 'teamIds',
              cell: ({ teamIds }) => getStatus(teamIds.length > 0),
              size: 64,
              disableSort: true,
            },
            {
              header: 'Profile Photo',
              value: 'status.profilePhoto',
              cell: ({ status }) => getStatus(status.profilePhoto),
              size: 64,
              disableSort: true,
            },
            {
              header: 'Notifications',
              value: 'status.notificationSettings',
              cell: ({ status }) => getStatus(status.notificationSettings),
              size: 64,
              disableSort: true,
            },
            {
              header: 'Calendar',
              value: 'status.connectedCalendar',
              cell: ({ status }) => getStatus(status.connectedCalendar),
              size: 64,
              disableSort: true,
            },
            {
              header: 'Conference link',
              value: 'status.conferenceLink',
              cell: ({ status }) => getStatus(status.conferenceLink),
              size: 64,
              disableSort: true,
            },
            {
              id: 'actions',
              render: (data) => (
                <Flex width={70}>
                  <IconButton
                    icon={faPenToSquare}
                    onClick={() => {
                      setSalesRepRow(data)
                      setIsModalOpen(true)
                      trackEvent('SALES_REP_EDIT_USER_MODAL_OPENED', {
                        userData: data,
                      })
                    }}
                    title="Edit"
                  />
                  <IconButton
                    onClick={() => {
                      trackEvent(
                        'SALES_REP_SETUP_EDIT_USER_PAGE_LINK_CLICKED',
                        {
                          userData: data,
                        }
                      )
                      openEditUserPage(data)
                    }}
                    icon={faUpRightFromSquare}
                    title="View in Manage teammates"
                  />
                </Flex>
              ),
              size: 70,
            },
          ]}
          isLoading={isLoading}
          disableBoxShadow
          pagination={{
            rowTotal: filteredRows.length ?? 10,
            defaultPagination: {
              pageIndex: 0,
              pageSize: 10,
            },
            enableClientSidePagination: true,
          }}
        />
      </Flex>
      {isModalOpen && (
        <SalesRepModal
          salesRepRow={salesRepRow}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </TableCard>
  )
}

export default SalesRepTable
